import React from 'react'
import { MATCH_FORMAT } from '../../../../constants/matchFormats'
import { SKILL_LEVELS } from '../../../../constants/skillLevels'

export default function LeagueSettingsSection({
  formData,
  errors,
  isEditMode,
  regions,
  showAddRegion,
  setShowAddRegion,
  newRegion,
  handleInputChange,
  handleNewRegionChange,
  handleAddRegion,
  isFieldDisabled,
  handleSkillLevelSelection,
}) {
  return (
    <div className="form-section">
      <h3>League Settings</h3>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="season">Season*</label>
          <input
            id="season"
            name="season"
            type="text"
            value={formData.season}
            onChange={handleInputChange}
            placeholder="e.g., Spring 2025"
            className={errors.season ? 'error' : ''}
            disabled={isFieldDisabled('season')}
            title={
              isFieldDisabled('season')
                ? `Cannot change season in ${formData.league_status} status`
                : ''
            }
          />
          {errors.season && (
            <span className="error-message">{errors.season}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="entry_fee">Entry Fee</label>
          <input
            id="entry_fee"
            name="entry_fee"
            type="number"
            min="0"
            step="0.01"
            value={formData.entry_fee}
            onChange={handleInputChange}
            disabled={isFieldDisabled('entry_fee')}
            title={
              isFieldDisabled('entry_fee')
                ? `Cannot change entry fee in ${formData.league_status} status`
                : ''
            }
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group region-select-group">
          <label htmlFor="region_id">Region</label>
          <div className="region-controls">
            <select
              id="region_id"
              name="region_id"
              value={formData.region_id}
              onChange={handleInputChange}
              className={errors.region_id ? 'error' : ''}
              disabled={isFieldDisabled('region_id')}
              title={
                isFieldDisabled('region_id')
                  ? `Cannot change region in ${formData.league_status} status`
                  : ''
              }
            >
              <option value="">Select Region</option>
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.city_area}, {region.state}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => setShowAddRegion(!showAddRegion)}
              disabled={isFieldDisabled('region_id')}
            >
              {showAddRegion ? 'Cancel' : 'Add Region'}
            </button>
          </div>
          {showAddRegion && (
            <div className="add-region-form">
              <div className="new-region-inputs">
                <input
                  type="text"
                  name="city_area"
                  value={newRegion.city_area}
                  onChange={handleNewRegionChange}
                  placeholder="City/Area"
                  className={errors.newRegion ? 'error' : ''}
                />
                <input
                  type="text"
                  name="state"
                  value={newRegion.state}
                  onChange={handleNewRegionChange}
                  placeholder="State"
                  className={errors.newRegion ? 'error' : ''}
                />
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={handleAddRegion}
                >
                  Add
                </button>
              </div>
              {errors.newRegion && (
                <span className="error-message">{errors.newRegion}</span>
              )}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="timezone">Timezone</label>
          <select
            id="timezone"
            name="timezone"
            value={formData.timezone}
            onChange={handleInputChange}
            disabled={isFieldDisabled('timezone')}
            title={
              isFieldDisabled('timezone')
                ? `Cannot change timezone in ${formData.league_status} status`
                : ''
            }
          >
            <option value="America/New_York">Eastern Time (US)</option>
            <option value="America/Chicago">Central Time (US)</option>
            <option value="America/Denver">Mountain Time (US)</option>
            <option value="America/Los_Angeles">Pacific Time (US)</option>
            <option value="UTC">UTC</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="match_format">Match Format</label>
          <select
            id="match_format"
            name="match_format"
            value={formData.match_format}
            onChange={handleInputChange}
            disabled={isFieldDisabled('match_format')}
            title={
              isFieldDisabled('match_format')
                ? `Cannot change match format in ${formData.league_status} status`
                : ''
            }
          >
            <option value={MATCH_FORMAT.TRADITIONAL}>
              Traditional (Best of 3 sets)
            </option>
            <option value={MATCH_FORMAT.PRO_SET_8}>
              Pro Set 8 (Single set to 8, win by 2)
            </option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="max_registrations">Maximum Registrations</label>
          <input
            id="max_registrations"
            name="max_registrations"
            type="number"
            min="0"
            value={formData.max_registrations}
            onChange={handleInputChange}
            disabled={isFieldDisabled('max_registrations')}
            title={
              isFieldDisabled('max_registrations')
                ? `Cannot change max registrations in ${formData.league_status} status`
                : ''
            }
          />
        </div>

        <div className="form-group">
          <label htmlFor="playoff_min_matches_required">
            Playoff Eligibility (Min Matches)
          </label>
          <input
            id="playoff_min_matches_required"
            name="playoff_min_matches_required"
            type="number"
            min="1"
            className="form-control"
            value={formData.playoff_min_matches_required}
            onChange={handleInputChange}
            title="Minimum completed matches required for playoff eligibility"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="min_players_per_division">
            Min Players per Division
          </label>
          <input
            id="min_players_per_division"
            name="min_players_per_division"
            type="number"
            min="2"
            value={formData.min_players_per_division}
            onChange={handleInputChange}
            disabled={isFieldDisabled('min_players_per_division')}
            title={
              isFieldDisabled('min_players_per_division')
                ? `Cannot change min players in ${formData.league_status} status`
                : ''
            }
          />
        </div>

        <div className="form-group">
          <label htmlFor="max_players_per_division">
            Max Players per Division
          </label>
          <input
            id="max_players_per_division"
            name="max_players_per_division"
            type="number"
            min="2"
            value={formData.max_players_per_division}
            onChange={handleInputChange}
            disabled={isFieldDisabled('max_players_per_division')}
            title={
              isFieldDisabled('max_players_per_division')
                ? `Cannot change max players in ${formData.league_status} status`
                : ''
            }
          />
        </div>
      </div>

      {!isEditMode && (
        <div className="form-group">
          <label>Allowed Skill Levels*</label>
          <div className="d-flex flex-wrap gap-2">
            {SKILL_LEVELS.map((level) => (
              <label key={level.value} className="form-check-label">
                <input
                  type="checkbox"
                  checked={formData.allowed_skill_levels.includes(level.value)}
                  onChange={(e) =>
                    handleSkillLevelSelection(level.value, e.target.checked)
                  }
                  data-testid={`allowed-skill-level-${level.value}`}
                />{' '}
                {level.label}
              </label>
            ))}
          </div>
          {errors.allowed_skill_levels && (
            <span className="error-message">{errors.allowed_skill_levels}</span>
          )}
        </div>
      )}
    </div>
  )
}
